import { AsyncPipe, Location, NgClass, NgForOf, NgIf } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { DateTime } from 'luxon';
import { firstValueFrom, Observable, Subscription } from 'rxjs';
import { group } from 'radash';

import { NzAlertComponent } from 'ng-zorro-antd/alert';
import { NzDescriptionsModule } from 'ng-zorro-antd/descriptions';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinComponent } from 'ng-zorro-antd/spin';
import { NzTableComponent, NzTableModule } from 'ng-zorro-antd/table';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzPopoverModule } from 'ng-zorro-antd/popover';

import { Locker } from '../../api/locker-api.class';
import { LockerApiService } from '../../api/locker-api.service';
import { ProvisionRequest } from '../../api/provision-request-api.class';
import { ProvisionRequestApiService } from '../../api/provision-request-api.service';
import { PersonModel } from '../../shared/person.model';
import { FormInputComponent } from '../../shared/ui/form-input/form-input.component';
import { FormTextareaComponent } from '../../shared/ui/form-textarea/form-textarea.component';
import { MainButtonComponent } from '../../shared/ui/main-button/main-button.component';
import { StoreService } from '../../store/store.service';
import { GetMatchcodesOutput } from '../../api/provision-request-api.types';
import { ClothingApiService } from '../../api/clothing-api.service';
import { ScannerService } from '../../shared/services/scanner.service';
import { NzInputDirective, NzInputGroupComponent } from 'ng-zorro-antd/input';
import { NzButtonComponent } from 'ng-zorro-antd/button';

@Component({
  selector: 'app-provision-detail',
  standalone: true,
  imports: [
    AsyncPipe,
    FormInputComponent,
    FormTextareaComponent,
    FormsModule,
    MainButtonComponent,
    NgClass,
    NgForOf,
    NgIf,
    NzDescriptionsModule,
    NzDropDownModule,
    NzIconModule,
    NzModalModule,
    NzSpinComponent,
    NzSelectModule,
    NzToolTipModule,
    NzTableModule,
    ReactiveFormsModule,
    NzAlertComponent,
    NzPopoverModule,
    NzInputDirective,
    NzInputGroupComponent,
    NzButtonComponent,
  ],
  templateUrl: './provision-detail.component.html',
  styleUrl: './provision-detail.component.scss',
})
export class ProvisionDetailComponent implements OnInit, OnDestroy {
  @ViewChild('table') table!: NzTableComponent<PersonModel>;
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  printPersons: PersonModel[] = [];
  searchValue = '';
  private searchDebounceTimeout: any;
  lockerSuggestions$: Observable<Locker[]>;
  requestPersons$: Observable<PersonModel[]>;
  selectedPerson: PersonModel | null = null;
  selectedLocker: Locker | null = null;
  checked = false;
  indeterminate = false;
  setOfCheckedId = new Set<number>();
  loading = false;
  modalIsVisible = false;
  clothingModalIsVisible = false;
  provisionRequest: ProvisionRequest | undefined;
  ausstattung: GetMatchcodesOutput | undefined;
  clothingSizes: GetMatchcodesOutput[] = [];
  persons: PersonModel[] = [];
  private allPersons: PersonModel[] = [];
  private personsSubscription?: Subscription;
  private sizeTextMap = new Map<number, string>();

  provisionRequestForm = this.fb.group({
    datum: this.fb.control<string | null>(null),
    modulBezeichnung: this.fb.control<string | null>(null),
    zeitraum: this.fb.control<string | null>(null),
    kleidungsart: this.fb.control<string | null>(null),
    spindart: this.fb.control<string | null>(null),
    ersteller: this.fb.control<string | null>(null),
    bemerkung: this.fb.control<string | null>(null),
  });
  scanStarTime = 0;
  scanSpeedThreshold = 50;
  scanTimeout: any;
  scannedCodeBuffer = '';
  private boundKeyDownHandler = this.onKeyDown.bind(this);

  listOfSortFilterOptions = {
    sortByLabCoatSize: (a: PersonModel, b: PersonModel) => {
      const valA = (a.groesseKittel ?? '').toString();
      const valB = (b.groesseKittel ?? '').toString();
      return valA.localeCompare(valB);
    },
  };

  constructor(
    private route: ActivatedRoute,
    private location: Location,
    private lockerApiService: LockerApiService,
    private provisionApiService: ProvisionRequestApiService,
    private clothingApiService: ClothingApiService,
    private scannerService: ScannerService,
    private store: StoreService,
    private message: NzMessageService,
    private fb: FormBuilder,
  ) {
    this.provisionRequest = this.route.snapshot.data['provisionRequest'];
    this.requestPersons$ = store.select(
      (state) => state.provisionRequests.activeProvisionRequestPersons,
    );
    this.lockerSuggestions$ = store.select(
      (state) =>
        state.provisionRequests.activeProvisionRequestLockerSuggestions,
    );
  }

  async ngOnInit() {
    window.addEventListener('keydown', this.boundKeyDownHandler);

    const {
      provisionRequests: {
        updateActiveProvisionRequest,
        updateActiveProvisionRequestPersons,
        setActiveProvisionRequestPersons,
      },
    } = this.store.getActions();

    updateActiveProvisionRequestPersons([]);

    this.personsSubscription = this.requestPersons$.subscribe((persons) => {
      this.allPersons = persons;
      this.applyFilter();
    });

    // Build size Lookup-Map
    this.clothingSizes = await this.clothingApiService.getClothingSizes();
    this.sizeTextMap.clear();
    for (const size of this.clothingSizes) {
      const code = Number(size.codenr);
      this.sizeTextMap.set(code, size.text ?? String(code));
    }

    updateActiveProvisionRequest(this.provisionRequest!);
    await setActiveProvisionRequestPersons();

    if (this.provisionRequest) {
      await this.prefillForm(this.provisionRequest);
    }
    if (this.provisionRequest?.beendetAm === null) {
      await this.suggestLockers();
    }
  }

  ngOnDestroy() {
    window.removeEventListener('keydown', this.boundKeyDownHandler);
    this.personsSubscription?.unsubscribe();
  }

  onKeyDown(event: KeyboardEvent) {
    if (!this.clothingModalIsVisible) return;

    const currentTime = DateTime.now().toMillis();
    if (this.scannedCodeBuffer === '') {
      this.scanStarTime = currentTime;
    }

    this.scannedCodeBuffer += event.key;

    clearTimeout(this.scanTimeout);
    this.scanTimeout = setTimeout(async () => {
      const scannedCode = this.scannedCodeBuffer.trim();
      this.scannedCodeBuffer = '';

      if (scannedCode) {
        const scanDuration = DateTime.now().toMillis() - this.scanStarTime;
        const avgCharTime = scanDuration / scannedCode.length;

        if (avgCharTime < this.scanSpeedThreshold) {
          const lowerCode = scannedCode.toLocaleLowerCase();

          if (lowerCode !== 'backspace' && lowerCode !== 'enter') {
            if (this.selectedPerson) {
              await this.scannerService.scanClothing(
                scannedCode,
                this.selectedPerson.id,
              );
              const {
                provisionRequests: { updateClothesOnProvisionPerson },
              } = this.store.getActions();
              await updateClothesOnProvisionPerson(this.selectedPerson.id);
              this.selectedPerson =
                this.allPersons.find(
                  (person) => person.id === this.selectedPerson?.id,
                ) ?? this.selectedPerson;
            } else {
              this.searchValue = scannedCode;
              await this.onSearch();
            }
          }
        }
      }
    }, 100);
  }

  navigateBack() {
    // this.router.navigate(['provisions']);
    this.location.back();
  }

  formatDate(date: string) {
    return DateTime.fromISO(date).toFormat('D', { locale: 'de' });
  }

  async showModal(person: PersonModel): Promise<void> {
    this.selectedPerson = person;
    await this.handleLockerLogic(this.selectedPerson);
    const lockers = await firstValueFrom(this.lockerSuggestions$);
    const tempLocker = lockers.find((locker) => locker.id === person.spindId);
    if (tempLocker) {
      this.selectedLocker = tempLocker;
    }
    this.modalIsVisible = true;
  }

  async showClothingModal(person: PersonModel): Promise<void> {
    this.selectedPerson = person;
    this.clothingModalIsVisible = true;
  }

  handleOk(): void {
    const {
      provisionRequests: { updateActiveProvisionRequestPerson },
    } = this.store.getActions();
    if (this.selectedPerson && this.selectedLocker) {
      const updatedPerson = {
        ...this.selectedPerson,
        spind: {
          bezeichnung: this.selectedLocker.bezeichnung,
          spindId: this.selectedLocker.id,
          schluessel_ausgegeben_am: null,
          zweitschluessel_ausgegeben_am: null,
        },
        spindBezeichnung: this.selectedLocker.bezeichnung,
        spindId: this.selectedLocker.id,
        status: 'Spind vorgeschlagen',
      };
      updateActiveProvisionRequestPerson(updatedPerson);
    }
    this.modalIsVisible = false;
  }

  handleCancel(): void {
    this.clothingModalIsVisible = false;
    this.modalIsVisible = false;
    this.searchValue = '';
    this.onSearch();
    setTimeout(() => this.searchInput?.nativeElement?.focus());
  }

  async suggestLockers() {
    const {
      provisionRequests: {
        setActiveProvisionRequestLockerSuggestions,
        updateActiveProvisionRequestPersons,
      },
    } = this.store.getActions();

    const params = {
      spindart: this.provisionRequest?.rawLockerType,
      status: 1,
    };
    await setActiveProvisionRequestLockerSuggestions(params);

    const genderMap: Record<
      'Männlich' | 'Weiblich' | 'Divers',
      { lockers: Locker[]; persons: PersonModel[] }
    > = {
      Männlich: { lockers: [], persons: [] },
      Weiblich: { lockers: [], persons: [] },
      Divers: { lockers: [], persons: [] },
    };

    // Retrieve locker suggestions and distribute into gender map
    const lockers = await firstValueFrom(this.lockerSuggestions$);
    lockers.forEach((locker) => {
      if (locker.geschlecht !== 'Unbekannt' && locker.geschlecht in genderMap) {
        genderMap[locker.geschlecht as keyof typeof genderMap].lockers.push(
          locker,
        );
      }
    });

    // Retrieve persons and distribute into gender map
    const persons = await firstValueFrom(
      this.store.select(
        (state) => state.provisionRequests.activeProvisionRequestPersons,
      ),
    );

    persons.forEach((person) => {
      if (person.geschlecht !== 'Unbekannt' && person.geschlecht in genderMap) {
        genderMap[person.geschlecht as keyof typeof genderMap].persons.push(
          person,
        );
      }
    });

    (Object.keys(genderMap) as (keyof typeof genderMap)[]).forEach((gender) => {
      genderMap[gender].persons = this.handlingLockerAssigning(
        genderMap[gender].persons,
        genderMap[gender].lockers,
      );
    });

    const combinedArray = [
      ...genderMap.Männlich.persons,
      ...genderMap.Weiblich.persons,
      ...genderMap.Divers.persons,
    ];
    updateActiveProvisionRequestPersons(combinedArray);
  }

  handlingLockerAssigning(personsArray: PersonModel[], lockers: Locker[]) {
    const moduleLocation = this.provisionRequest?.rawSpindstandort;
    if (!moduleLocation) {
      return personsArray; // No module location provided, return as is
    }

    const lockersGrouped = this.groupLockersByLocation(lockers);

    const locationsSorted = [
      moduleLocation,
      ...Object.keys(lockersGrouped)
        .map((location) => Number(location))
        .filter((location) => location !== moduleLocation),
    ];

    for (const location of locationsSorted) {
      const lockersAtLocation = lockersGrouped[location] ?? [];
      if (lockersAtLocation.length >= personsArray.length * 2) {
        // Assign with one locker spacing
        personsArray = this.distributeLockers(
          personsArray,
          lockersAtLocation,
          2, // Spacing of 1 locker
          false,
        );
        return personsArray; // All lockers assigned
      } else if (lockersAtLocation.length >= personsArray.length) {
        // Assign as much spacing as possible and then fill up
        personsArray = this.distributeLockers(
          personsArray,
          lockersAtLocation,
          2, // Attempt spacing
          true,
        );

        this.message.warning(
          'Nicht genügend Spinde vom Typ "' +
            personsArray[0].geschlecht +
            '" verfügbar, um einen Spind Abstand zu gewährleisten. <br> ' +
            'Es wurde versucht einen Platz freizulassen und ansonsten aufzufüllen.',
          { nzDuration: 8000 },
        );
        return personsArray;
      }
      // else {
      //   this.message.warning(
      //     'Es sind nicht genügend Spinde vom Typ "' +
      //       personsArray[0].geschlecht +
      //       '" im Gebäude verfügbar, in dem das Modul stattfindet. <br> ' +
      //       'Den Teilnehmer*innen wurden Spinde aus dem nächsten Gebäude zugewiesen',
      //     { nzDuration: 8000 },
      //   );
      // }
    }

    return personsArray;
  }

  distributeLockers(
    personsArray: PersonModel[],
    lockersAtLocation: Locker[],
    indexMultiplier: number,
    partialAssignment: boolean,
  ): PersonModel[] {
    let remainingPersons: PersonModel[] = [];
    const remainingLockers: Locker[] = [];

    personsArray.forEach((person, index) => {
      if (person.status !== 'Zugewiesen') {
        const lockerIndex = index * indexMultiplier;
        const locker = lockersAtLocation[lockerIndex];

        if (locker) {
          person.spindBezeichnung = locker.bezeichnung;
          person.spindId = locker.id;
          person.status = 'Spind vorgeschlagen';
        } else if (partialAssignment) {
          // Push people who did not get a locker
          remainingPersons.push(person);
        }
      }
      remainingLockers.push(lockersAtLocation[index * indexMultiplier - 1]);
    });
    remainingLockers.shift();
    // Recur for remaining persons if partial assignment is allowed
    if (partialAssignment && remainingPersons.length > 0) {
      remainingPersons = this.distributeLockers(
        remainingPersons,
        remainingLockers,
        1,
        false,
      );
      personsArray.filter((person) => remainingPersons.includes(person));
    }

    return personsArray;
  }

  groupLockersByLocation(lockers: Locker[]) {
    return group(lockers, (locker) => locker.rawStandort);
  }

  async acceptLockers() {
    const resultArray = [];
    const requestPersons = this.store.getStore().getState()
      .provisionRequests.activeProvisionRequestPersons;

    const selectedPersons = requestPersons.filter((person) =>
      this.setOfCheckedId.has(person.id),
    );
    for (const person of selectedPersons) {
      if (person.status === 'Spind vorgeschlagen') {
        resultArray.push(await this.acceptLocker(person, true));
      }
    }
    if (resultArray.every((result) => result === true)) {
      this.message.success('Spinde erfolgreich zugewiesen.');
    }
  }

  async onReleaseLockerClick() {
    const requestPersons = this.store.getStore().getState()
      .provisionRequests.activeProvisionRequestPersons;

    const selectedPersons = requestPersons.filter((person) =>
      this.setOfCheckedId.has(person.id),
    );

    for (const person of selectedPersons) {
      if (person.spindId) {
        await this.releaseLocker(person, true);
      }
    }
    this.message.success('Schlüssel erfolgreich zurückgegeben.');
  }

  async handleLockerLogic(person: PersonModel): Promise<void> {
    const {
      provisionRequests: { setActiveProvisionRequestLockerSuggestions },
    } = this.store.getActions();

    const genderMap: Record<string, number> = {
      Männlich: 1,
      Weiblich: 2,
      Divers: 3,
    };

    const geschlecht = genderMap[person.geschlecht] || 0;

    const params = {
      spindart: this.provisionRequest?.rawLockerType,
      status: 1,
      geschlecht,
    };

    await setActiveProvisionRequestLockerSuggestions(params);
  }

  async acceptLocker(person: PersonModel, bulkTag: boolean) {
    const result = await this.lockerApiService.assignLocker(person.spindId!, {
      spindpersonId: person.id,
    });
    await this.lockerApiService.handoutLockerKey(person.spindId!, {
      ausgabedatum: DateTime.now().toISODate(),
    });

    const {
      provisionRequests: { updateActiveProvisionRequestPerson },
    } = this.store.getActions();

    const updatedPerson = {
      ...person,
      status: 'Zugewiesen',
    };
    updateActiveProvisionRequestPerson(updatedPerson);
    if (!bulkTag) {
      this.message.success('Spind erfolgreich zugewiesen.');
    }
    return result;
  }

  async releaseLocker(person: PersonModel, bulkTag: boolean) {
    if (person.spindId) {
      await this.lockerApiService.withdrawLockerKey(person.spindId, {
        ruecknahmedatum: DateTime.now().toISODate(),
      });
      try {
        await this.lockerApiService.releaseLocker(person.spindId);
      } catch {
        this.message.warning(
          'Spind konnte nicht freigeben werden, da vermutlich ein Zweitschlüssel ausgegeben war.',
        );
      }
    }

    const {
      provisionRequests: {
        updateActiveProvisionRequestPerson,
        updateActiveProvisionRequest,
      },
    } = this.store.getActions();

    const updatedPerson = {
      ...person,
      status: 'Spind vorgeschlagen',
    };
    updateActiveProvisionRequestPerson(updatedPerson);

    if (!bulkTag) {
      this.message.success('Schlüssel erfolgreich zurückgegeben.');
    }
    const closeRequest = await this.checkIfRequestShouldBeClosed();

    if (closeRequest) {
      try {
        await this.provisionApiService.closeProvisionRequest(
          this.provisionRequest!.id,
          {
            beendet_am: DateTime.now().toISODate(),
          },
        );
        const updatedRequest = await firstValueFrom(
          this.provisionApiService.getProvisionRequest(
            this.provisionRequest!.id,
          ),
        );
        this.provisionRequest = updatedRequest;
        updateActiveProvisionRequest(updatedRequest);
        this.message.success('Versorgungsanfrage wurde erfolgreich beendet.');
      } catch {
        this.message.error('Versorgungsanfrage konnte nicht beendet werden.');
      }
    }
  }

  async prefillForm(provisionRequest: ProvisionRequest) {
    const kleidungsausstattungen =
      await this.provisionApiService.getClothingEquipment();
    this.ausstattung = kleidungsausstattungen.find(
      (i) => i.codenr === Number(provisionRequest.kleidungsart),
    );
    this.provisionRequestForm.patchValue({
      zeitraum:
        'Vom ' +
        this.formatDate(provisionRequest.datumVon) +
        ' bis zum ' +
        this.formatDate(provisionRequest.datumBis),
      ersteller: provisionRequest.ersteller.name,
      bemerkung: provisionRequest.bemerkung,
      datum: this.formatDate(provisionRequest.datum),
      modulBezeichnung: provisionRequest.modul?.bezeichnung ?? 'Kein Modul',
      kleidungsart:
        this.ausstattung?.text === undefined
          ? 'Keine Kleidungssausstattung'
          : this.ausstattung?.text,
      spindart: provisionRequest.spindart,
    });
  }

  updateCheckedSet(id: number, checked: boolean): void {
    if (checked) {
      this.setOfCheckedId.add(id);
    } else {
      this.setOfCheckedId.delete(id);
    }
  }

  async onItemChecked(id: number, checked: boolean): Promise<void> {
    this.updateCheckedSet(id, checked);
    await this.refreshCheckedStatus();
  }

  async onAllChecked(value: boolean): Promise<void> {
    const persons = await firstValueFrom(this.requestPersons$);
    persons.forEach((item) => this.updateCheckedSet(item.id, value));
    await this.refreshCheckedStatus();
  }

  async refreshCheckedStatus(): Promise<void> {
    const persons = await firstValueFrom(this.requestPersons$);
    this.checked = persons.every((item) => this.setOfCheckedId.has(item.id));
    this.indeterminate =
      persons.some((item) => this.setOfCheckedId.has(item.id)) && !this.checked;
  }

  activeState() {
    if (this.provisionRequest?.beendetAm) {
      return false;
    } else {
      return this.checked || this.indeterminate;
    }
  }

  async checkIfRequestShouldBeClosed() {
    const persons = await firstValueFrom(this.requestPersons$);
    return !persons.some((person) => person.status === 'Zugewiesen');
  }

  // Return text for sizes
  getSizeText(code: number | string | null | undefined): string | null {
    if (code === null || code === undefined || code === '') {
      return null;
    }
    const num = Number(code);
    if (Number.isNaN(num)) {
      return null;
    }
    return this.sizeTextMap.get(num) ?? null;
  }

  async closeClothingRequest() {
    try {
      await this.provisionApiService.closeProvisionRequest(
        this.provisionRequest!.id,
        {
          beendet_am: DateTime.now().toISODate(),
        },
      );
      const updatedRequest = await firstValueFrom(
        this.provisionApiService.getProvisionRequest(this.provisionRequest!.id),
      );
      this.provisionRequest = updatedRequest;
      const {
        provisionRequests: { updateActiveProvisionRequest },
      } = this.store.getActions();
      updateActiveProvisionRequest(updatedRequest);
      this.message.success('Versorgungsanfrage wurde erfolgreich beendet.');
    } catch {
      this.message.error('Versorgungsanfrage konnte nicht beendet werden.');
    }
  }

  printTable(): void {
    const sorted = this.table.data;
    this.printPersons = [...sorted];
    setTimeout(() => window.print(), 0);
  }

  onSearchInput(): void {
    this.onSearch();

    clearTimeout(this.searchDebounceTimeout);
    this.searchDebounceTimeout = setTimeout(() => {
      this.tryOpenModalFromScan();
    }, 150);
  }

  onSearch(): void {
    this.applyFilter();
  }

  private applyFilter(): void {
    const raw = this.searchValue.trim().toLowerCase();
    const normalized = raw.startsWith('p-') ? raw.replace(/^p-/, '') : raw;

    if (!normalized) {
      this.persons = [...this.allPersons];
      return;
    }

    this.persons = this.allPersons.filter((person) =>
      person.id.toString().includes(normalized),
    );
  }

  private async tryOpenModalFromScan(): Promise<void> {
    const raw = this.searchValue.trim().toLowerCase();
    const normalized = raw.startsWith('p-') ? raw.replace(/^p-/, '') : raw;

    if (!normalized || !normalized.match(/^\d+$/)) return;

    const exactMatch = this.persons.find(
      (person) => person.id.toString() === normalized,
    );

    if (exactMatch) {
      await this.showClothingModal(exactMatch);
    }
  }
}
